import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "fwdRef", "tagName"];
import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { buildVersionedClass } from '@op/opds.opux-utilities';
const classMap = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
  xsOffset: 'xs--offset',
  smOffset: 'sm--offset',
  mdOffset: 'md--offset',
  lgOffset: 'lg--offset',
  xlOffset: 'xl--offset'
};
function isValidKey(value) {
  return value in classMap;
}

/**
 * `<Col>` components are used to structure the layout inside the `<Row>` component
 * and enable dividing space horizontally into columns (per breakpoint).
 *
 * Each of the `xs`, `sm`, `md`, `lg` and `xl` column widths and offsets are applied
 * by finding the largest applicable style, so e.g. `<Col xs={12} lg={6} />`
 * will be 12 columns wide at breakpoints up to `md`, and 6 columns above that.
 *
 * For the props that set a size in columns, you may use `true` for automatic sizing.
 * Additional props (including `ref`) will be forwarded to the HTML element.
 */
export const Col = _ref => {
  let {
      className,
      fwdRef,
      tagName: Tag = 'div'
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  let cl = [className, buildVersionedClass('ds-col')];
  for (const key of Object.keys(classMap)) {
    if (isValidKey(key)) {
      const value = props[key];
      if (value !== undefined) {
        const cm = classMap[key];
        if (typeof value === 'number' && isFinite(value) && Math.floor(value) === value) {
          cl.push(buildVersionedClass("ds-col--" + cm + "-" + value));
        } else {
          cl.push(buildVersionedClass("ds-col--" + cm));
        }
        delete props[key];
      }
    }
  }
  if (props.first) {
    const size = props.first === true ? 'xs' : props.first;
    cl.push(buildVersionedClass("ds-col--first-" + size));
    delete props.first;
  }
  if (props.last) {
    const size = props.last === true ? 'xs' : props.last;
    cl.push(buildVersionedClass("ds-col--last-" + size));
    delete props.last;
  }
  if (props.hidden) {
    if (Array.isArray(props.hidden)) {
      cl = cl.concat(props.hidden.map(size => buildVersionedClass("ds-col--hidden-" + size)));
    } else {
      cl.push(buildVersionedClass("ds-col--hidden-" + props.hidden));
    }
    delete props.hidden;
  }

  // @ts-expect-error -- Typing fwdRef as HTMLElement will not work with any tag therefore we just ignore the error
  return /*#__PURE__*/React.createElement(Tag, _extends({}, props, {
    className: classNames(cl),
    ref: fwdRef
  }));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Col, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Col';
export default Fwd;